import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { isEmpty } from 'lodash';

import Select from '@joinforma/ui/components/Select';
import Option from '@joinforma/ui/components/Option';

import { PretaxWallet } from 'contexts/PretaxContext/types';
import { PretaxAccountType, PretaxAccountV2Type } from 'constants/accounts';
import { EVENTS } from 'constants/events';
import { getWalletDisplayName } from 'utils/company';
import date_helper from 'utils/date-helper';
import {
  getPretaxAccountLabel,
  translateWalletTypeToDisplay,
} from 'utils/pretaxAccount';
import { StoreRoutes } from 'utils/routeGenerator';
import { logRudderstackEvent } from 'utils/rudderstack';

interface PretaxAccount {
  account_status_code: string;
  account_type: string;
}

interface Wallet {
  company_wallet_configuration: {
    is_twic_store_enabled?: boolean;
    wallet_name: string;
  };
  id: string;
}

const Dropdown = styled(Select)`
  width: 100%;
`;

const AccountSelect = ({
  eligibleWallets,
  hasFsaLpf,
  isFormaPretax = false,
  label = 'Browse Account',
  pretaxAccounts,
}: {
  eligibleWallets: Array<Wallet>;
  hasFsaLpf: boolean;
  isFormaPretax?: boolean;
  label?: string;
  pretaxAccounts: Array<PretaxAccount> | Array<PretaxWallet>;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const accountType = useMemo(() => {
    const account = pretaxAccounts[0];
    if (account && !isFormaPretax) {
      // Alegeus combined LPFSA & HSA
      if (hasFsaLpf) {
        return 'LPFSA & HSA';
      }

      // Alegeus pretax accounts
      if (Object.hasOwn(account, 'account_type')) {
        return (account as PretaxAccount).account_type;
      }
    }

    return '';
  }, [pretaxAccounts, hasFsaLpf, isFormaPretax]);

  const renderPretaxWallets = () => {
    if (!isFormaPretax) {
      return hasFsaLpf ? (
        <Option
          key="LPFSAHSA"
          value={JSON.stringify({
            type: 'pretax',
            label: PretaxAccountType.LPFSAHSA,
          })}
        >
          {'LPFSA & HSA'}
        </Option>
      ) : (
        <Option
          key={getPretaxAccountLabel({ account_type: accountType })}
          value={JSON.stringify({
            type: 'pretax',
            label: getPretaxAccountLabel({
              account_type: accountType,
            }),
          })}
        >
          {getPretaxAccountLabel({ account_type: accountType })}
        </Option>
      );
    }

    return (pretaxAccounts as Array<PretaxWallet>).map((wallet) => {
      const walletType = translateWalletTypeToDisplay(
        wallet.wallet_type_id as PretaxAccountV2Type
      );

      // Sometimes it's possible to have 2+ active pretax accounts from different years
      const startYear =
        wallet.company_wallet_configuration &&
        date_helper(wallet.company_wallet_configuration.start_date).format(
          'YYYY'
        );
      const walletName = `${startYear} ${walletType}`;

      return (
        <Option
          key={wallet.id}
          value={JSON.stringify({
            id: wallet.id,
            wallet_name: walletName,
            type: 'pretax',
            label: walletType,
            year: startYear,
          })}
        >
          {walletName}
        </Option>
      );
    });
  };

  const handleAccountChange = (value: string) => {
    const {
      id: walletId,
      label: pretaxAccount,
      type,
      wallet_name: account,
      year,
    } = JSON.parse(value);
    logRudderstackEvent(EVENTS.StoreLandingDropdownClicked, {
      'Current Page': location.pathname,
      'Dropdown Type': 'Account Filter',
      'Selection Name': type === 'pretax' ? pretaxAccount : account,
    });

    const route =
      type === 'pretax'
        ? StoreRoutes.detailPretax({ pretax_account: pretaxAccount, year })
        : StoreRoutes.accountBrowser({ walletId });
    navigate(route, {
      state: { ...location.state, origin: 'dropdown_selection' },
    });
  };

  return (
    <Dropdown
      label={label}
      name="account-filter"
      onChange={handleAccountChange}
    >
      {eligibleWallets.map(
        (
          { id, company_wallet_configuration: { wallet_name: walletName } },
          index
        ) => (
          <Option
            key={`${id} ${index}`}
            value={JSON.stringify({ id, wallet_name: walletName })}
          >
            {getWalletDisplayName(walletName)}
          </Option>
        )
      )}
      {!isEmpty(pretaxAccounts) && renderPretaxWallets()}
    </Dropdown>
  );
};

export default AccountSelect;
